/*
 * ct-gift-terms.js
 *
 * Pagina Actievoorwaarden: bouwt per merk een kaart met alle actieve
 * cadeauacties, rechtstreeks uit de regels van EG Auto Add to Cart.
 *
 * Bron: ctEasyGift.tiers() (assets/ct-eg-rules.js), dezelfde parser als de
 * cadeauladder in de winkelmand. Giftgegevens (naam, foto, waarde, vendor,
 * voorraad) via ctEasyGift.giftInfo(), gedeelde cache met de ladder.
 *
 * Opbouw:
 * - Groep = regels met dezelfde triggercollecties (groupKey), net als in
 *   de ladder. Per groep één kaart.
 * - Kaart = het blok 'Merk' uit de sectie waarvan de triggercollectie in
 *   de groep zit (data-collection). Geen blok: een kaart uit <template>
 *   met de vendor van het eerste cadeau als merknaam en een link naar de
 *   vendor-collectie van Shopify (/collections/vendors?q=<vendor>).
 * - Treden oplopend op drempel; bedrag-treden eerst, daarna aantal-treden.
 *   Per trede: voorwaarde, cadeaus (foto, naam, waarde), keuzeregel,
 *   looptijd, 'tijdelijk uitverkocht'.
 * - Blokken zonder actieve regel blijven hidden. Geen enkele regel: de
 *   statusregel toont 'geen cadeauacties'.
 *
 * Rendert opnieuw zodra giftgegevens binnenkomen (gebundeld per frame).
 * Faalt dit script, dan staan de vaste voorwaarden er nog (Liquid).
 *
 * Teksten komen als data-attributen mee uit sections/ct-gift-terms.liquid.
 * Styling: assets/ct-gift-terms.css.
 *
 * Bewust geen ES module: <script defer> vanuit de sectie, na
 * ct-eg-rules.js (dat staat in de <head> via snippets/preload-js.liquid;
 * defer bewaart de documentvolgorde).
 */
(function () {
  'use strict';

  var ROOT_SEL = '[data-ct-terms]';
  var ICON_GIFT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="3" y="8" width="18" height="4" rx="1"></rect><path d="M12 8v13"></path><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"></path><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"></path></svg>';

  var root = null;
  var t = {};
  var rootPath = '/';
  var locale = 'nl';
  var els = {};
  var autoCards = {};   /* groupKey -> kaart uit <template> */
  var frame = null;

  /* ------------------------------------------------------------------ */
  /* Hulpfuncties                                                        */
  /* ------------------------------------------------------------------ */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function money(cents) {
    var v = Math.max(0, Math.round(cents)) / 100;
    var s = v.toFixed(2).replace('.', ',');
    if (s.slice(-3) === ',00') s = s.slice(0, -3);
    return '\u20ac' + s;
  }

  function tpl(key, vars) {
    var s = t[key] || '';
    Object.keys(vars || {}).forEach(function (k) {
      s = s.replace(new RegExp('{{\\s*' + k + '\\s*}}', 'g'), vars[k]);
    });
    return s;
  }

  function fmtDate(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
    } catch (e) {
      return d.toLocaleDateString();
    }
  }

  function scheduleRender() {
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = null;
      render();
    });
  }

  function giftFor(key, fallbackTitle) {
    var info = window.ctEasyGift ? ctEasyGift.giftInfo(key, scheduleRender) : null;
    return {
      key: key,
      loaded: !!info,
      title: (info && info.title) || fallbackTitle || '',
      image: info ? info.image : '',
      price: info ? info.price : 0,
      vendor: info ? info.vendor : '',
      available: info ? info.available : true
    };
  }

  function tierGifts(tier) {
    var out = [];
    if (tier.choice) {
      tier.choice.products.forEach(function (p) {
        out.push(p.handle ? giftFor('h:' + p.handle, p.title) : { key: '', loaded: true, title: p.title || '', image: '', price: 0, vendor: '', available: true });
      });
    } else {
      tier.gifts.forEach(function (vid) { out.push(giftFor('v:' + vid, '')); });
    }
    return out;
  }

  /* ------------------------------------------------------------------ */
  /* Groeperen                                                           */
  /* ------------------------------------------------------------------ */

  function groupTiers(tiers) {
    var groups = {};
    tiers.forEach(function (tier) {
      var g = groups[tier.groupKey];
      if (!g) g = groups[tier.groupKey] = { key: tier.groupKey, collections: tier.collections, tiers: [] };
      g.tiers.push(tier);
    });
    Object.keys(groups).forEach(function (k) {
      groups[k].tiers.sort(function (a, b) {
        if (a.thresholdType !== b.thresholdType) return a.thresholdType === 'value' ? -1 : 1;
        return a.threshold - b.threshold;
      });
    });
    return groups;
  }

  /* Merknaam en link van een groep: eerst het blok, anders de vendor van
     het eerste cadeau waarvan de gegevens binnen zijn. */
  function groupMeta(g, card) {
    var name = card.getAttribute('data-name') || '';
    var url = card.getAttribute('data-url') || '';
    var vendor = '';
    g.tiers.some(function (tier) {
      return tierGifts(tier).some(function (gift) {
        if (gift.vendor) { vendor = gift.vendor; return true; }
        return false;
      });
    });
    if (!name) name = vendor || (g.key === 'cart' ? '' : t.brand);
    if (!url && vendor) url = rootPath + 'collections/vendors?q=' + encodeURIComponent(vendor);
    return { name: name, url: url, vendor: vendor };
  }

  /* ------------------------------------------------------------------ */
  /* Renderen                                                            */
  /* ------------------------------------------------------------------ */

  function condHtml(tier, name) {
    if (tier.groupKey === 'cart') return tpl('cart', { amount: money(tier.threshold) });
    if (tier.thresholdType === 'quantity') return tpl('qty', { count: tier.threshold, brand: esc(name) });
    if (tier.threshold === 0) return tpl('any', { brand: esc(name) });
    return tpl('from', { amount: money(tier.threshold), brand: esc(name) });
  }

  function giftHtml(gift, choice) {
    var media = gift.image ? '<img src="' + esc(gift.image) + '" alt="" loading="lazy" width="72" height="72">' : ICON_GIFT;
    var worth = gift.price > 0 ? '<span class="ct-terms__worth">' + esc(tpl('worth', { amount: money(gift.price) })) + '</span>' : '';
    var soldout = gift.loaded && !gift.available ? '<span class="ct-terms__soldout">' + esc(t.soldout) + '</span>' : '';
    return '<li class="ct-terms__gift' + (gift.loaded && !gift.available ? ' is-soldout' : '') + '">' +
      '<span class="ct-terms__gift-media">' + media + '</span>' +
      '<span class="ct-terms__gift-text">' +
        '<span class="ct-terms__gift-title">' + esc(gift.title || t.gift) + '</span>' +
        worth + soldout +
      '</span>' +
    '</li>';
  }

  function tierHtml(tier, name) {
    var gifts = tierGifts(tier);
    var label;
    if (tier.choice) {
      label = tier.choice.count > 1 ? tpl('choice', { count: tier.choice.count }) : t.choiceOne;
    } else {
      label = t.gift;
    }
    var until = tier.schedule && tier.schedule.ends ? fmtDate(tier.schedule.ends) : '';
    return '<li class="ct-terms__tier">' +
      '<div class="ct-terms__cond">' + condHtml(tier, name) + '</div>' +
      '<div class="ct-terms__label">' + esc(label) + '</div>' +
      '<ul class="ct-terms__gifts">' + gifts.map(function (g) { return giftHtml(g, !!tier.choice); }).join('') + '</ul>' +
      (until ? '<div class="ct-terms__until">' + esc(tpl('until', { date: until })) + '</div>' : '') +
    '</li>';
  }

  function cardFor(g) {
    /* Blok met een triggercollectie uit deze groep */
    var blocks = root.querySelectorAll('[data-ct-terms-brand][data-collection]');
    for (var i = 0; i < blocks.length; i++) {
      if (g.collections.indexOf(blocks[i].getAttribute('data-collection')) !== -1) return blocks[i];
    }
    /* Anders een automatische kaart, één per groep */
    if (autoCards[g.key]) return autoCards[g.key];
    var tmpl = root.querySelector('[data-ct-terms-template]');
    if (!tmpl || !tmpl.content) return null;
    var card = tmpl.content.firstElementChild.cloneNode(true);
    card.setAttribute('data-auto', g.key);
    els.brands.appendChild(card);
    autoCards[g.key] = card;
    return card;
  }

  function render() {
    if (!root || !window.ctEasyGift) return;
    var tiers = ctEasyGift.tiers();
    var groups = groupTiers(tiers);
    var keys = Object.keys(groups);
    var used = [];

    keys.forEach(function (k) {
      var g = groups[k];
      var card = cardFor(g);
      if (!card) return;
      var meta = groupMeta(g, card);

      /* Automatische kaart: naam en knop invullen */
      var nameEl = card.querySelector('[data-ct-terms-name]');
      if (nameEl) nameEl.textContent = meta.name;
      var cta = card.querySelector('[data-ct-terms-cta]');
      if (cta) {
        if (meta.url && meta.name) {
          cta.href = meta.url;
          cta.textContent = tpl('shop', { brand: meta.name });
          cta.hidden = false;
        } else {
          cta.hidden = true;
        }
      }

      var list = card.querySelector('[data-ct-terms-tiers]');
      if (list) {
        list.innerHTML = '<ol class="ct-terms__tier-list">' + g.tiers.map(function (tier) { return tierHtml(tier, meta.name); }).join('') + '</ol>';
      }
      card.hidden = false;
      used.push(card);
    });

    /* Kaarten zonder actieve regel verbergen */
    root.querySelectorAll('[data-ct-terms-brand]').forEach(function (card) {
      if (used.indexOf(card) === -1 && !card.closest('template')) card.hidden = true;
    });

    if (els.state) {
      if (keys.length) {
        els.state.hidden = true;
      } else {
        els.state.textContent = t.none;
        els.state.hidden = false;
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Start                                                               */
  /* ------------------------------------------------------------------ */

  function init() {
    root = document.querySelector(ROOT_SEL);
    if (!root) return;
    var d = root.dataset;
    rootPath = d.root || '/';
    if (rootPath.charAt(rootPath.length - 1) !== '/') rootPath += '/';
    locale = d.locale || 'nl';
    t = {
      from: d.tFrom, any: d.tAny, qty: d.tQty, cart: d.tCart, gift: d.tGift,
      choice: d.tChoice, choiceOne: d.tChoiceOne, worth: d.tWorth, soldout: d.tSoldout,
      until: d.tUntil, shop: d.tShop, brand: d.tBrand, none: d.tNone
    };
    els.state = root.querySelector('[data-ct-terms-state]');
    els.brands = root.querySelector('[data-ct-terms-brands]');

    if (!window.ctEasyGift) {
      /* Parser niet geladen: statusregel weg, vaste voorwaarden blijven */
      if (els.state) els.state.hidden = true;
      return;
    }
    if (window.ctEasyGift.setRoot) ctEasyGift.setRoot(rootPath);

    ctEasyGift.ready(function () { render(); });

    /* Komt de app niet (adblocker, embed uit), dan blijft 'laden' anders
       eeuwig staan. Na 6 s de statusregel verbergen; de kaarten komen
       alsnog als de meta later toch verschijnt. */
    window.setTimeout(function () {
      if (els.state && !els.state.hidden && !ctEasyGift.read()) els.state.hidden = true;
    }, 6000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
